import {
  getTarifaValor,
  computeInsuranceFromTarifa,
  computeCautionFromTarifa,
  PER_M3_SUBGRUPO_IDS,
} from "./ddpImportTarifasPure";

export const PER_M3_IMPORT_SUBGRUPOS = PER_M3_SUBGRUPO_IDS;

export function parseLooseDecimal(val) {
  return parseFloat(String(val ?? "").replace(",", ".")) || 0;
}

export function safeDecimal(val, decimals = 4) {
  return Number(parseFloat(val).toFixed(decimals));
}

export function resolveVolPerUnit(presupuestoVol, allLines = []) {
  const volPerUnitRaw = parseLooseDecimal(presupuestoVol);
  const somePerM3Line = allLines.find(
    (l) =>
      Number(l.ESCANDALLO_GRUPOS) === 13 &&
      PER_M3_IMPORT_SUBGRUPOS.has(Number(l.ESCANDALLO_SUBGRUPO)),
  );
  return volPerUnitRaw > 0
    ? volPerUnitRaw
    : parseLooseDecimal(somePerM3Line?.Un);
}

export function computeSubtotalProveedor(allLines = []) {
  return allLines
    .filter((l) => Number(l.ESCANDALLO_GRUPOS) !== 13)
    .reduce((acc, l) => acc + parseLooseDecimal(l.CosteConvertido), 0);
}

export function computeInsuranceConvCost(subtotalProveedor, tarifas = []) {
  return computeInsuranceFromTarifa(subtotalProveedor, tarifas).conv;
}

export function computeLclUnitConvCost(volPerUnit, lclCostCat) {
  return safeDecimal(volPerUnit * (lclCostCat || 0));
}

export function computeImportDutyBase(
  subtotalProveedor,
  insuranceConvCost,
  lclUnitConvCost,
) {
  return subtotalProveedor + insuranceConvCost + lclUnitConvCost;
}

export function resolveDutyPct(pais, materialSubgrupo) {
  const p = String(pais || "").toLowerCase();
  const dutyChina = parseLooseDecimal(materialSubgrupo?.DutiesChina ?? 0);
  const dutyIndia = parseLooseDecimal(materialSubgrupo?.DutiesIndia ?? 0);
  if (p.includes("china")) return dutyChina;
  if (p.includes("india")) return dutyIndia;
  return 0;
}

export function getSubgrupoCostFromLineOverride(id, allLines = []) {
  const existing = allLines.find(
    (l) =>
      Number(l.ESCANDALLO_GRUPOS) === 13 &&
      Number(l.ESCANDALLO_SUBGRUPO) === Number(id),
  );
  const existingCost = parseLooseDecimal(existing?.Coste);
  if (existingCost > 0) return existingCost;
  return 0;
}

/** Coste unitario desde tarifas DDP (tabla DdpImportTarifas). Override manual en línea prevalece. */
export function getSubgrupoCostFromTarifas(id, allLines = [], tarifas = []) {
  const lineOverride = getSubgrupoCostFromLineOverride(id, allLines);
  if (lineOverride > 0) return lineOverride;
  return getTarifaValor(tarifas, id);
}

/** @deprecated Usar getSubgrupoCostFromTarifas */
export function getSubgrupoCostFromCatalog(
  id,
  allLines = [],
  escandalloSubgrupos = [],
  tarifas = [],
) {
  const fromTarifas = getSubgrupoCostFromTarifas(id, allLines, tarifas);
  if (fromTarifas > 0) return fromTarifas;

  const rowGlobal = escandalloSubgrupos.find(
    (s) => Number(s.ID_SUBGRUPO) === Number(id),
  );
  return parseLooseDecimal(rowGlobal?.Coste);
}

export function computeDutyAmount(dutyBase, dutyPct, arancelCosteConvertido) {
  const conv = parseLooseDecimal(arancelCosteConvertido);
  if (conv > 0) return conv;
  return safeDecimal(parseLooseDecimal(dutyBase) * (parseLooseDecimal(dutyPct) / 100), 2);
}

export function resolveEffectiveDutyPct(dutyPct, allLines = []) {
  const parsed = parseLooseDecimal(dutyPct);
  if (parsed > 0) return parsed;

  const arancelLine = allLines.find(
    (l) =>
      Number(l?.ESCANDALLO_GRUPOS) === 13 &&
      Number(l?.ESCANDALLO_SUBGRUPO) === 192,
  );
  return parseLooseDecimal(arancelLine?.Un);
}

export function resolveMaterialSubgrupo(
  idSubgrupo,
  materialSubgrupos = [],
  escandalloSubgrupos = [],
) {
  const id = Number(idSubgrupo);
  if (!id) return null;

  const fromMaterial = materialSubgrupos.find(
    (s) => Number(s?.ID_SUBGRUPO) === id,
  );
  if (fromMaterial) return fromMaterial;

  return (
    escandalloSubgrupos.find((s) => Number(s?.ID_SUBGRUPO) === id) || null
  );
}

export function resolveProveedorForDuty(
  presupuesto = {},
  tiposProveedores = [],
  allLines = [],
  defaultSupplier = null,
) {
  const supplierCodesFromLines = allLines
    .filter((l) => Number(l?.ESCANDALLO_GRUPOS) !== 13)
    .flatMap((l) => [l?.PROVEEDOR_ESCANDALLO, l?.Proveedor])
    .filter(Boolean);

  const candidateCodes = [
    presupuesto?.IdProveedor,
    defaultSupplier,
    ...supplierCodesFromLines,
  ];

  for (const code of candidateCodes) {
    const match = tiposProveedores.find(
      (p) =>
        String(p?.CODPROV) === String(code) ||
        String(p?.N_PROVEEDOR).toLowerCase() === String(code).toLowerCase(),
    );
    if (match) return match;
  }
  return null;
}

export function computeImportRecalcBases({
  presupuesto = {},
  allLines = [],
  tiposProveedores = [],
  escandalloSubgrupos = [],
  materialSubgrupos = [],
  defaultSupplier = null,
  tarifas = [],
}) {
  const volPerUnit = resolveVolPerUnit(presupuesto.VOL, allLines);
  const subtotalProveedor = computeSubtotalProveedor(allLines);
  const insurance = computeInsuranceFromTarifa(subtotalProveedor, tarifas);
  const insuranceConvCost = insurance.conv;
  const insuranceBase = insurance.base;
  const lclCostCat = getSubgrupoCostFromTarifas(203, allLines, tarifas);
  const handlingCostCat = getSubgrupoCostFromTarifas(204, allLines, tarifas);
  const deliveryCostCat = getSubgrupoCostFromTarifas(198, allLines, tarifas);
  const lclUnitConvCost = computeLclUnitConvCost(volPerUnit, lclCostCat);
  const refreshedSubtotalBaseImport = computeImportDutyBase(
    subtotalProveedor,
    insuranceConvCost,
    lclUnitConvCost,
  );
  const proveedor = resolveProveedorForDuty(
    presupuesto,
    tiposProveedores,
    allLines,
    defaultSupplier,
  );
  const materialSubgrupo = resolveMaterialSubgrupo(
    presupuesto.IdSubgrupo,
    materialSubgrupos,
    escandalloSubgrupos,
  );
  const dutyPct = resolveDutyPct(proveedor?.PAIS, materialSubgrupo);

  return {
    volPerUnit,
    subtotalProveedor,
    insuranceBase,
    insuranceConvCost,
    insuranceUn: insurance.un,
    cautionUn: computeCautionFromTarifa(0, tarifas).un,
    lclCostCat,
    handlingCostCat,
    deliveryCostCat,
    lclUnitConvCost,
    refreshedSubtotalBaseImport,
    dutyPct,
  };
}
